import React, { useContext } from "react";
import styled from "styled-components";
import {
  EXTEND_SUBSCRIPTION_DURATIONS,
  LanguageNameSpaces,
  SubscriptionDuration,
} from "@shared/types";
import { DURATION_TO_SECONDARY_DISPLAY } from "@shared/features/settings/subscription/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import NewLookSegmented from "features/settings/NewLook/NewLookSegmented.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface NewLookPeriodToggleProps {
  promoCodeDiscount?: Record<SubscriptionDuration, number>;
}

/**
 * The billing period toggle of the plan picker (new look): a pill segmented
 * control with the annual saving (or the applied promo code) underneath.
 */
const NewLookPeriodToggle = ({
  promoCodeDiscount,
}: NewLookPeriodToggleProps) => {
  const { duration, setDuration, promoCode } = useContext(
    BuySubscriptionContext
  )!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const showAnnuallyDiscount =
    !!promoCode &&
    duration !== 360 &&
    !promoCodeDiscount?.[duration] &&
    !!promoCodeDiscount?.[360];

  const hint = promoCode ? (
    <>
      <PromoCode>{promoCode.toUpperCase()}</PromoCode>
      {showAnnuallyDiscount
        ? t("period_buttons.annually_discount", {
            percent: promoCodeDiscount?.[360],
          })
        : t("period_buttons.discount_applied", {
            percent: promoCodeDiscount?.[duration] || 0,
          })}
    </>
  ) : (
    <>
      {t("period_buttons.annually_default")}{" "}
      <strong>{t("period_buttons.months_free")}</strong>
    </>
  );

  return (
    <Wrapper>
      <NewLookSegmented<SubscriptionDuration>
        variant="large"
        value={duration}
        onChange={(value) => setDuration(value)}
        options={EXTEND_SUBSCRIPTION_DURATIONS.map((option) => ({
          value: option,
          label: t(DURATION_TO_SECONDARY_DISPLAY[option]),
        }))}
      />
      <Hint>{hint}</Hint>
    </Wrapper>
  );
};

export default NewLookPeriodToggle;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;
const Hint = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).up};
  text-align: center;
  strong {
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  }
`;
const PromoCode = styled.span`
  display: inline-block;
  margin-right: 8px;
  padding: 1px 8px;
  border: 1px dashed ${(p) => p.theme.colors.green};
  border-radius: 6px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  letter-spacing: 0.02em;
`;
